import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarDays, Lock, MapPin } from "lucide-react";
import { GOAL_LABELS } from "@omina/core";

import { BLUEPRINT_FOR } from "@/lib/blueprint";

import { useEventRadar, useSettings } from "@/lib/queries";
import { cn } from "@/lib/utils";

/**
 * The payoff — step 6.
 *
 * Five rooms, three readable and two behind a lock. The point of the screen is
 * the moment she sees that Omina actually found something, so the visible ones
 * come first and are complete: a name, a date, a place and why it was picked.
 *
 * **The locked rooms are real.** They are the next two entries of the same
 * ranked list, fetched and scored exactly like the three above them, and the
 * count in the nudge is the count that exists. Blurring invented rows would be
 * the plausible-looking lie rule #2 is about — and it is the one lie a paywall
 * can never recover from, because she buys, sees what was behind it, and knows.
 *
 * So the honest failure mode is *fewer than five*. If the base only has three
 * rooms for her, she sees three and no lock, because there is nothing being
 * withheld. A paywall over an empty pool is the worst version of this screen.
 */
export const Route = createFileRoute("/matches")({
  component: Matches,
});

/**
 * How many a founder sees without paying.
 *
 * Three is enough to prove the product works — the whole argument of the
 * screen — and few enough that a full week of rooms is worth paying for. One
 * would prove nothing and five would be the whole answer.
 */
const FREE_ROOMS = 3;
/** Never more than this on the screen, locked ones included. */
const SHOWN = 5;


function Matches() {
  const settings = useSettings();
  const goal = settings.data?.activeGoal;
  const radar = useEventRadar(goal, 45, Boolean(goal));

  /** Checkout does not exist yet — see the note where this is used. */
  const [tappedUpgrade, setTappedUpgrade] = useState(false);

  if (settings.isPending || radar.isPending) {
    return (
      <div className="matchwrap">
        <p className="lede">Looking for your rooms…</p>
      </div>
    );
  }

  const all = radar.data?.candidates ?? [];
  const rooms = all.slice(0, SHOWN);
  const open = rooms.slice(0, FREE_ROOMS);
  const locked = rooms.slice(FREE_ROOMS);

  /**
   * Nothing found. Said plainly, with the reason and a way forward, rather
   * than an empty list under a triumphant heading — and emphatically without a
   * paywall, because there is nothing behind it (rule #2).
   */
  if (rooms.length === 0) {
    return (
      <div className="matchwrap">
        <div className="eyebrow">Your rooms</div>
        <h1 className="tk tk-want">Nothing yet, honestly</h1>
        <p className="lede">
          Omina watches event calendars by region and the base does not cover
          yours yet. Nothing here is worth showing you until it does.
        </p>
        <Link to="/feed" className="btn">
          See what is on the radar
        </Link>
      </div>
    );
  }

  return (
    <div className="matchwrap">
      <div className="eyebrow">Your rooms</div>
      <h1 className="tk tk-want">Here is where you should be</h1>
      <p className="lede matchlede">
        Ranked for {goal ? GOAL_LABELS[goal].title.toLowerCase() : "your goal"},
        near you, in the next six weeks.
      </p>

      <ul className="matchlist">
        {open.map((r) => (
          <li key={r.id}>
            <article className="matchcard">
              <h2 className="matchname">{r.name}</h2>
              <p className="matchmeta">
                <span>
                  <CalendarDays size={12} aria-hidden />
                  {formatDate(r.date)}
                </span>
                {placeOf(r.location) && (
                  <span>
                    <MapPin size={12} aria-hidden />
                    {placeOf(r.location)}
                  </span>
                )}
              </p>
              {r.why && <p className="matchwhy">{r.why}</p>}
            </article>
          </li>
        ))}

        {/*
          The locked ones.

          `aria-hidden` and no focusable content inside: a screen reader would
          otherwise read out the very names the blur is hiding, which makes the
          lock decorative rather than real. The count below carries the
          information instead, and it is announced.
        */}
        {locked.map((r) => (
          <li key={r.id}>
            <article className="matchcard islocked" aria-hidden>
              <h2 className="matchname">{r.name}</h2>
              <p className="matchmeta">
                <span>
                  <CalendarDays size={12} aria-hidden />
                  {formatDate(r.date)}
                </span>
              </p>
              {r.why && <p className="matchwhy">{r.why}</p>}
            </article>
          </li>
        ))}
      </ul>

      {locked.length > 0 && (
        <div className="matchgate">
          <span className="matchgateicon" aria-hidden>
            <Lock size={15} />
          </span>
          <p className="matchgatetitle">
            {locked.length} more {locked.length === 1 ? "room" : "rooms"} matched
            your goal
          </p>
          <p className="hint">
            The free plan shows {FREE_ROOMS}. Upgrade to see every room Omina
            finds, every week.
          </p>
          {/*
            Straight into the questions — no landing page in between.

            There used to be one per track, explaining what the blueprint was
            for. It was a tap that answered a question she had not asked yet;
            the same framing now opens the conversation, where it arrives as
            someone telling her what they are about to do.

            All four tracks have their own set of questions, so the destination
            is the goal she picked. The fallback stays for the case where
            settings have not resolved yet — a link with no goal would send her
            to the wrong track's questions, which is worse than a moment's wait.
          */}
          {goal ? (
            <Link to={BLUEPRINT_FOR[goal]} className="btn">
              Unlock all rooms
            </Link>
          ) : (
            <button type="button" className="btn" onClick={() => setTappedUpgrade(true)}>
              Unlock all rooms
            </button>
          )}
          {/*
            Checkout is not built. Rather than a button that silently does
            nothing — which reads as broken — it says so on tap. This is the
            one line on the screen that has to change before anyone outside the
            team sees it.
          */}
          {tappedUpgrade && (
            <p className="hint matchsoon">Checkout is not connected yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** "Wed, 10 Sep" — enough to decide, nothing more. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** Same rule as the curation card: half of these are registration links, and a
 *  40-character URL is not a place. A URL collapses to its host. */
function placeOf(location: string | null | undefined): string | null {
  const raw = location?.trim();
  if (!raw) return null;
  if (!/^https?:\/\//i.test(raw)) return raw;
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}
